import { motion } from "framer-motion";
import { HardHat, Building2, Star } from "lucide-react";
import Footer from "../components/navigation/footer";

export default function OurStory() {
  return (
    <div>
      <section className="min-h-screen bg-[#F6F4EA] text-[#111] pt-28 px-6 md:px-20 space-y-28">
        {/* Hero Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto pt-10">
          {[
            {
              title: "Custom Home Builds",
              desc: "Tailored construction from design to handover",
              img: "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/exterior.JPG",
            },
            {
              title: "Renovations & Extensions",
              desc: "Transform your space with our expert team",
              img: "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/42Sutton/42sutton1.png",
            },
            {
              title: "Project Management",
              desc: "Reliable timelines and seamless delivery",
              img: "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/Greg.webp",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="relative rounded-xl overflow-hidden shadow-lg group"
            >
              <img
                src={item.img}
                alt={item.title}
                className="w-full h-60 object-cover transition-transform group-hover:scale-105 duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 p-6 flex flex-col justify-end text-white">
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-sm text-gray-200">{item.desc} →</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Headline */}
        <div className="max-w-7xl mx-auto">
          <h2 className="text-6xl md:text-5xl font-semibold font-interTight leading-tight max-w-4xl mx-auto">
            Building with care, precision, and pride no matter the project.
          </h2>
        </div>

        {/* Benefits Row */}
        <div className="grid md:grid-cols-3 gap-8 text-center max-w-7xl mx-auto text-gray-700 text-sm">
          <div className="flex flex-col items-center space-y-2">
            <HardHat className="w-6 h-6" />
            <p>Licensed builder.</p>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <Building2 className="w-6 h-6" />
            <p>End-to-end management of residential projects.</p>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <Star className="w-6 h-6" />
            <p>Outstanding craftsmanship and service.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <motion.img
            src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/Construction.png"
            alt="Construction Site"
            className="rounded-xl shadow-md w-full object-cover"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-semibold mb-4">Who We Are</h2>
            <p className="text-lg leading-relaxed text-gray-700">
              ArkBuild is a Sydney-based construction company focused on
              building premium custom homes, renovations, and developments. Our
              passion is in the details, our pride is in the craftsmanship.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-semibold mb-4">Our Mission</h2>
            <p className="text-lg leading-relaxed text-gray-700">
              To make homebuilding a transparent and inspired experience. We
              combine innovation and industry insight to deliver stress-free
              construction from design through delivery.
            </p>
          </motion.div>
          <motion.img
            src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/Design.webp"
            alt="Blueprint"
            className="rounded-xl shadow-md w-full object-cover"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.img
            src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/1Madonna/exterior.JPG"
            alt="Modern Home"
            className="rounded-xl shadow-md w-full object-cover"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-semibold mb-4">Why Choose Us</h2>
            <ul className="space-y-3 list-disc pl-5 text-gray-700 text-lg">
              <li>Complete project transparency</li>
              <li>
                Smarter builds through cost-effective design and construction
              </li>
              <li>Full-scope delivery from concept to completion</li>
              <li>Certified sustainable building practices</li>
              <li>Handpicked, trusted trade partners</li>
            </ul>
          </motion.div>
        </div>

        <div className="pb-20 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl font-semibold mb-6">Meet the Director</h2>
            <p className="text-lg leading-relaxed max-w-4xl mx-auto text-gray-700">
              Hands-on leadership backed by real results.
            </p>

            <div className="mt-12 flex flex-col md:flex-row gap-10 text-left">
              {/* Director Bio */}
              <div className="md:w-1/2">
                <div className="group transition-transform">
                  <img
                    src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/Greg.webp"
                    alt="Gregory"
                    className="rounded-xl shadow-md mb-4 w-full h-80 object-cover object-top"
                  />
                  <h3 className="text-xl font-medium">Gregory Kaskaniotis</h3>
                  <p className="text-sm text-gray-600 pb-2">
                    Director & Construction Manager
                  </p>
                  <hr />
                  <p className="text-sm text-black pt-6">
                    Gregory Kaskanitois is an experienced construction
                    professional with over 7 years in project management and
                    site coordination. He has led high-density residential
                    developments across Sydney and the Gold Coast, delivering
                    projects on time and to a high standard. Gregory obtained
                    his Bachelor of Construction Management with High
                    Distinction from Western Sydney University in 2019 and holds
                    a NSW Builder's License. He brings strong expertise in
                    compliance, procurement, and stakeholder engagement.
                  </p>
                </div>
              </div>

              {/* Project Image Grid */}
              <div className="md:w-1/2 grid grid-cols-2 gap-4">
                <img
                  src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/experience-1.webp"
                  alt="Project 1"
                  className="rounded-lg object-cover h-48 w-full"
                />
                <img
                  src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/experience-2.webp"
                  alt="Project 2"
                  className="rounded-lg object-cover h-48 w-full"
                />
                <video
                  src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/videos/experience-3.mp4"
                  alt="Project 3"
                  loop
                  autoPlay
                  muted
                  className="rounded-lg object-cover h-48 w-full"
                />
                <img
                  src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/experience-4.webp"
                  alt="Project 4"
                  className="rounded-lg object-cover h-48 w-full"
                />
              </div>
            </div>
          </motion.div>

          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 px-4 pt-20">
            {/* Left Image */}
            <div className="w-full md:w-1/2 p-8">
              <img
                src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/master-builders-association-logo.png"
                alt="Left"
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            {/* Right Image */}
            <div className="w-full md:w-1/2 p-8">
              <img
                src="https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/hia_member.png"
                alt="Right"
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
